<div class="p-4 sm:p-6 space-y-4 sm:space-y-5">
  <!-- Header -->
  <div class="flex items-center justify-between">
    <h1 class="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
      {{ 'ADMIN.CUSTOMERS.TITLE' | translate }}
    </h1>
    <button
      (click)="openCreate()"
      class="flex items-center gap-2 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
    >
      <lucide-icon [img]="UserPlus" class="w-4 h-4"></lucide-icon>
      <span class="hidden sm:inline">{{ 'ADMIN.CUSTOMERS.ADD' | translate }}</span>
      <span class="sm:hidden">{{ 'COMMON.ADD' | translate }}</span>
    </button>
  </div>

  <!-- Filters -->
  <div class="flex flex-wrap items-center gap-2 sm:gap-3">
    <div class="relative flex-1 min-w-0" style="min-width: 160px; max-width: 320px">
      <lucide-icon
        [img]="Search"
        class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
      ></lucide-icon>
      <input
        type="text"
        [placeholder]="'ADMIN.CUSTOMERS.SEARCH' | translate"
        class="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
        (input)="onSearch($any($event.target).value)"
      />
    </div>
    <div class="flex items-center gap-1.5">
      <button
        (click)="setFilter(undefined)"
        class="px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm rounded-lg border transition-colors"
        [class]="
          filterActive() === undefined
            ? 'bg-green-600 text-white border-green-600'
            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700'
        "
      >
        {{ 'ADMIN.CUSTOMERS.ALL' | translate }}
      </button>
      <button
        (click)="setFilter(true)"
        class="px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm rounded-lg border transition-colors"
        [class]="
          filterActive() === true
            ? 'bg-green-600 text-white border-green-600'
            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700'
        "
      >
        {{ 'ADMIN.CUSTOMERS.ACTIVE' | translate }}
      </button>
      <button
        (click)="setFilter(false)"
        class="px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm rounded-lg border transition-colors"
        [class]="
          filterActive() === false
            ? 'bg-red-600 text-white border-red-600'
            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700'
        "
      >
        {{ 'ADMIN.CUSTOMERS.INACTIVE' | translate }}
      </button>
    </div>
    <button
      (click)="loadCustomers()"
      class="p-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
    >
      <lucide-icon [img]="RefreshCw" class="w-4 h-4"></lucide-icon>
    </button>
  </div>

  @if (loading()) {
    <div
      class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-10 text-center"
    >
      <p class="text-gray-500">{{ 'COMMON.LOADING' | translate }}</p>
    </div>
  } @else {
    <!-- Mobile card view (hidden on sm+) -->
    <div class="sm:hidden space-y-3">
      @for (c of customers(); track c.tenantId) {
        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <p class="font-semibold text-gray-900 dark:text-white text-sm truncate">
                {{ c.fullName }}
              </p>
              <p class="text-xs text-gray-500 dark:text-gray-400">{{ c.phoneNumber }}</p>
              @if (c.tenantType === 'Demo') {
                <span
                  class="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium mt-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                >
                  Demo
                </span>
              } @else {
                <span
                  class="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium mt-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                >
                  Mijoz
                </span>
              }
              @if (c.businessName) {
                <p class="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{{ c.businessName }}</p>
              }
            </div>
            @if (c.isActive) {
              <span
                class="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-400 shrink-0"
              >
                <lucide-icon [img]="CheckCircle" class="w-3 h-3"></lucide-icon
                >{{ 'ADMIN.CUSTOMERS.ACTIVE' | translate }}
              </span>
            } @else {
              <span
                class="inline-flex items-center gap-1 text-xs text-red-700 dark:text-red-400 shrink-0"
              >
                <lucide-icon [img]="XCircle" class="w-3 h-3"></lucide-icon
                >{{ 'ADMIN.CUSTOMERS.INACTIVE' | translate }}
              </span>
            }
          </div>
          <div class="flex items-center justify-between text-xs">
            <div>
              @if (c.subscriptionPlanName) {
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                >
                  {{ c.subscriptionPlanName }}
                </span>
              } @else {
                <span class="text-gray-400">{{ 'ADMIN.CUSTOMERS.NO_PLAN' | translate }}</span>
              }
            </div>
            <div>
              @if (c.isSubscriptionExpired) {
                <span class="inline-flex items-center gap-1 text-red-500 text-xs">
                  <lucide-icon [img]="AlertCircle" class="w-3 h-3"></lucide-icon
                  >{{ 'ADMIN.CUSTOMERS.EXPIRED' | translate }}
                </span>
              } @else {
                <span class="text-gray-500 dark:text-gray-400">{{
                  formatDate(c.subscriptionEndDate)
                }}</span>
              }
            </div>
          </div>
          <div class="flex gap-2">
            <button
              (click)="openSubscription(c)"
              class="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <lucide-icon [img]="CreditCard" class="w-3 h-3"></lucide-icon>
              {{ 'ADMIN.CUSTOMERS.ACTIONS.PLAN' | translate }}
            </button>
            <button
              (click)="openExtend(c)"
              class="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs rounded-lg border border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
            >
              <lucide-icon [img]="CalendarPlus" class="w-3 h-3"></lucide-icon>
              {{ 'ADMIN.CUSTOMERS.ACTIONS.EXTEND' | translate }}
            </button>
            <button
              (click)="openPayments(c)"
              class="px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              [title]="'ADMIN.CUSTOMERS.ACTIONS.PAYMENTS' | translate"
            >
              <lucide-icon [img]="History" class="w-3.5 h-3.5"></lucide-icon>
            </button>
            <button
              (click)="openEdit(c)"
              class="px-3 py-1.5 text-xs rounded-lg border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              title="Tahrirlash"
            >
              <lucide-icon [img]="Pencil" class="w-3.5 h-3.5"></lucide-icon>
            </button>
            <button
              (click)="openImport(c)"
              class="px-3 py-1.5 text-xs rounded-lg border border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
              title="Mahsulot import"
            >
              <lucide-icon [img]="Package" class="w-3.5 h-3.5"></lucide-icon>
            </button>
            <button
              (click)="openCheck(c)"
              class="px-3 py-1.5 text-xs rounded-lg border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
              title="Mahsulotlarni tekshirish"
            >
              <lucide-icon [img]="ShieldCheck" class="w-3.5 h-3.5"></lucide-icon>
            </button>
            @if (isSuperAdmin()) {
              <button
                (click)="openDelete(c)"
                class="px-3 py-1.5 text-xs rounded-lg border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                title="O‘chirish"
              >
                <lucide-icon [img]="Trash2" class="w-3.5 h-3.5"></lucide-icon>
              </button>
            }
          </div>
        </div>
      } @empty {
        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-10 text-center text-gray-500 text-sm"
        >
          {{ 'ADMIN.CUSTOMERS.EMPTY' | translate }}
        </div>
      }
    </div>

    <!-- Desktop table view (hidden on mobile) -->
    <div
      class="hidden sm:block bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
    >
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th
                class="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap"
              >
                {{ 'ADMIN.CUSTOMERS.COLS.CUSTOMER' | translate }}
              </th>
              <th
                class="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap"
              >
                {{ 'ADMIN.CUSTOMERS.COLS.BUSINESS' | translate }}
              </th>
              <th
                class="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap"
              >
                Turi
              </th>
              <th
                class="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap"
              >
                {{ 'ADMIN.CUSTOMERS.COLS.PLAN' | translate }}
              </th>
              <th
                class="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap"
              >
                {{ 'ADMIN.CUSTOMERS.COLS.END_DATE' | translate }}
              </th>
              <th
                class="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap"
              >
                {{ 'ADMIN.CUSTOMERS.COLS.STATUS' | translate }}
              </th>
              <th
                class="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap"
              >
                {{ 'ADMIN.CUSTOMERS.COLS.DATE' | translate }}
              </th>
              <th class="px-4 py-3 w-24"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            @for (c of customers(); track c.tenantId) {
              <tr class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <td class="px-4 py-3">
                  <p class="font-medium text-gray-900 dark:text-white">{{ c.fullName }}</p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">{{ c.phoneNumber }}</p>
                </td>
                <td class="px-4 py-3 text-gray-700 dark:text-gray-300 max-w-32 truncate">
                  {{ c.businessName }}
                </td>
                <td class="px-4 py-3">
                  @if (c.tenantType === 'Demo') {
                    <span
                      class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                    >
                      Demo
                    </span>
                  } @else {
                    <span
                      class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                    >
                      Mijoz
                    </span>
                  }
                </td>
                <td class="px-4 py-3">
                  @if (c.subscriptionPlanName) {
                    <span
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                    >
                      {{ c.subscriptionPlanName }}
                    </span>
                    @if (c.monthlyPrice) {
                      <p class="text-xs text-gray-400 mt-0.5">
                        {{ formatMoney(c.monthlyPrice) }}/oy
                      </p>
                    }
                  } @else {
                    <span class="text-gray-400 text-xs">--</span>
                  }
                </td>
                <td class="px-4 py-3">
                  @if (c.isSubscriptionExpired) {
                    <span
                      class="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400 font-medium"
                    >
                      <lucide-icon [img]="AlertCircle" class="w-3 h-3"></lucide-icon
                      >{{ 'ADMIN.CUSTOMERS.EXPIRED' | translate }}
                    </span>
                  } @else {
                    <span class="text-xs text-gray-600 dark:text-gray-400">{{
                      formatDate(c.subscriptionEndDate)
                    }}</span>
                  }
                </td>
                <td class="px-4 py-3">
                  @if (c.isActive) {
                    <span
                      class="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-400"
                    >
                      <lucide-icon [img]="CheckCircle" class="w-3 h-3"></lucide-icon
                      >{{ 'ADMIN.CUSTOMERS.ACTIVE' | translate }}
                    </span>
                  } @else {
                    <span
                      class="inline-flex items-center gap-1 text-xs text-red-700 dark:text-red-400"
                    >
                      <lucide-icon [img]="XCircle" class="w-3 h-3"></lucide-icon
                      >{{ 'ADMIN.CUSTOMERS.INACTIVE' | translate }}
                    </span>
                  }
                </td>
                <td class="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {{ formatDate(c.createdAt) }}
                </td>
                <td class="px-4 py-3">
                  <div class="flex items-center gap-1">
                    <button
                      (click)="openSubscription(c)"
                      class="flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
                    >
                      <lucide-icon [img]="CreditCard" class="w-3 h-3"></lucide-icon>
                      {{ 'ADMIN.CUSTOMERS.ACTIONS.PLAN' | translate }}
                    </button>
                    <button
                      (click)="openExtend(c)"
                      class="flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors whitespace-nowrap"
                    >
                      <lucide-icon [img]="CalendarPlus" class="w-3 h-3"></lucide-icon>
                      {{ 'ADMIN.CUSTOMERS.ACTIONS.EXTEND' | translate }}
                    </button>
                    <button
                      (click)="openPayments(c)"
                      class="p-1 rounded-md border border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      [title]="'ADMIN.CUSTOMERS.ACTIONS.PAYMENTS' | translate"
                    >
                      <lucide-icon [img]="History" class="w-3.5 h-3.5"></lucide-icon>
                    </button>
                    <button
                      (click)="openEdit(c)"
                      class="p-1 rounded-md border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      title="Tahrirlash"
                    >
                      <lucide-icon [img]="Pencil" class="w-3.5 h-3.5"></lucide-icon>
                    </button>
                    <button
                      (click)="openImport(c)"
                      class="p-1 rounded-md border border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                      title="Mahsulot import"
                    >
                      <lucide-icon [img]="Package" class="w-3.5 h-3.5"></lucide-icon>
                    </button>
                    <button
                      (click)="openCheck(c)"
                      class="p-1 rounded-md border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                      title="Mahsulotlarni tekshirish"
                    >
                      <lucide-icon [img]="ShieldCheck" class="w-3.5 h-3.5"></lucide-icon>
                    </button>
                    @if (isSuperAdmin()) {
                      <button
                        (click)="openDelete(c)"
                        class="p-1 rounded-md border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        title="O‘chirish"
                      >
                        <lucide-icon [img]="Trash2" class="w-3.5 h-3.5"></lucide-icon>
                      </button>
                    }
                  </div>
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="8" class="px-4 py-10 text-center text-gray-500 dark:text-gray-400">
                  {{ 'ADMIN.CUSTOMERS.EMPTY' | translate }}
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      @if (total() > pageSize) {
        <div
          class="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-700"
        >
          <p class="text-xs text-gray-500 dark:text-gray-400">
            {{ (page() - 1) * pageSize + 1 }}вЂ“{{
              page() * pageSize > total() ? total() : page() * pageSize
            }}
            / {{ total() }} ta
          </p>
          <div class="flex items-center gap-2">
            <button
              (click)="prevPage()"
              [disabled]="page() === 1"
              class="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <lucide-icon [img]="ChevronLeft" class="w-4 h-4"></lucide-icon>
            </button>
            <span class="text-sm text-gray-700 dark:text-gray-300"
              >{{ page() }} / {{ totalPages() }}</span
            >
            <button
              (click)="nextPage()"
              [disabled]="page() >= totalPages()"
              class="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <lucide-icon [img]="ChevronRight" class="w-4 h-4"></lucide-icon>
            </button>
          </div>
        </div>
      }
    </div>

    <!-- Mobile pagination -->
    @if (total() > pageSize) {
      <div class="sm:hidden flex items-center justify-between px-1">
        <p class="text-xs text-gray-500 dark:text-gray-400">
          {{ (page() - 1) * pageSize + 1 }}вЂ“{{
            page() * pageSize > total() ? total() : page() * pageSize
          }}
          / {{ total() }}
        </p>
        <div class="flex items-center gap-2">
          <button
            (click)="prevPage()"
            [disabled]="page() === 1"
            class="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <lucide-icon [img]="ChevronLeft" class="w-4 h-4"></lucide-icon>
          </button>
          <span class="text-sm text-gray-700 dark:text-gray-300"
            >{{ page() }} / {{ totalPages() }}</span
          >
          <button
            (click)="nextPage()"
            [disabled]="page() >= totalPages()"
            class="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <lucide-icon [img]="ChevronRight" class="w-4 h-4"></lucide-icon>
          </button>
        </div>
      </div>
    }
  }
</div>

<!-- Create Customer Dialog -->
@if (showCreateDialog()) {
  <div class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
    <div
      class="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-[90vh] flex flex-col"
    >
      <div
        class="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700 shrink-0"
      >
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
          {{ 'ADMIN.CUSTOMERS.FORM.TITLE' | translate }}
        </h2>
        <button
          (click)="showCreateDialog.set(false)"
          class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
        >
          <lucide-icon [img]="X" class="w-5 h-5 text-gray-500"></lucide-icon>
        </button>
      </div>
      <form
        [formGroup]="createForm"
        (ngSubmit)="submitCreate()"
        class="p-5 space-y-4 overflow-y-auto"
      >
        @if (errorMsg()) {
          <div
            class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400"
          >
            {{ errorMsg() }}
          </div>
        }
        <div class="grid grid-cols-2 gap-3">
          <div class="col-span-2">
            <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{
              'ADMIN.CUSTOMERS.FORM.FULL_NAME' | translate
            }}</label>
            <input
              formControlName="fullName"
              type="text"
              placeholder="Alisher Navoiy"
              class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <label
              for="cust-phone"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >{{ 'ADMIN.CUSTOMERS.FORM.PHONE' | translate }}</label
            >
            <div
              class="flex rounded-xl border border-gray-300 dark:border-gray-700 overflow-hidden focus-within:ring-2 focus-within:ring-green-500 bg-white dark:bg-gray-800"
            >
              <select
                [(ngModel)]="phoneCode"
                [ngModelOptions]="{ standalone: true }"
                class="shrink-0 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm pl-2 pr-1 border-r border-gray-300 dark:border-gray-600 outline-none cursor-pointer"
              >
                <option value="+998">🇺🇿 +998</option>
                <option value="+7">🇷🇺 +7</option>
                <option value="+992">🇹🇯 +992</option>
                <option value="+993">🇹🇲 +993</option>
                <option value="+996">🇰🇬 +996</option>
                <option value="+994">🇦🇿 +994</option>
              </select>
              <input
                id="cust-phone"
                formControlName="phoneNumber"
                type="tel"
                placeholder="90 123 45 67"
                class="flex-1 min-w-0 px-3 py-2.5 text-sm bg-transparent text-gray-900 dark:text-white placeholder:text-gray-400 outline-none"
              />
            </div>
          </div>
          <div>
            <label
              for="cust-pass"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >{{ 'ADMIN.CUSTOMERS.FORM.PASSWORD' | translate }}</label
            >
            <div class="relative">
              <input
                id="cust-pass"
                formControlName="password"
                [type]="showPassword ? 'text' : 'password'"
                [placeholder]="'ADMIN.CUSTOMERS.FORM.PASSWORD_MIN' | translate"
                class="w-full px-3 py-2.5 pr-10 text-sm border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <button
                type="button"
                (click)="showPassword = !showPassword"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <lucide-icon [img]="showPassword ? EyeOff : Eye" class="w-4 h-4"></lucide-icon>
              </button>
            </div>
          </div>
          <div>
            <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{
              'ADMIN.CUSTOMERS.FORM.GENDER' | translate
            }}</label>
            <select
              formControlName="gender"
              class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option [value]="0">{{ 'ADMIN.CUSTOMERS.FORM.MALE' | translate }}</option>
              <option [value]="1">{{ 'ADMIN.CUSTOMERS.FORM.FEMALE' | translate }}</option>
            </select>
          </div>
          <div>
            <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{
              'ADMIN.CUSTOMERS.FORM.BUSINESS' | translate
            }}</label>
            <input
              formControlName="businessName"
              type="text"
              [placeholder]="'ADMIN.CUSTOMERS.FORM.BUSINESS_HINT' | translate"
              class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div class="col-span-2">
            <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{
              'ADMIN.CUSTOMERS.FORM.PLAN' | translate
            }}</label>
            <select
              formControlName="subscriptionPlanId"
              class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">{{ 'ADMIN.CUSTOMERS.FORM.PLAN_DEFAULT' | translate }}</option>
              @for (plan of plans(); track plan.id) {
                <option [value]="plan.id">
                  {{ plan.name }} {{ plan.monthlyPrice | number: '1.0-0' }} UZS/oy
                </option>
              }
            </select>
          </div>
        </div>
        <div class="flex gap-3 pt-1">
          <button
            type="button"
            (click)="showCreateDialog.set(false)"
            class="flex-1 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            {{ 'ADMIN.CUSTOMERS.FORM.CANCEL' | translate }}
          </button>
          <button
            type="submit"
            [disabled]="createForm.invalid || submitting()"
            class="flex-1 py-2.5 text-sm bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 transition-colors font-medium"
          >
            {{
              submitting()
                ? ('ADMIN.CUSTOMERS.FORM.SAVING' | translate)
                : ('ADMIN.CUSTOMERS.FORM.CREATE' | translate)
            }}
          </button>
        </div>
      </form>
    </div>
  </div>
}

<!-- Assign Subscription Dialog -->
@if (showSubscriptionDialog()) {
  <div class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
    <div
      class="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-[90vh] flex flex-col"
    >
      <div
        class="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700 shrink-0"
      >
        <div>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
            {{ 'ADMIN.CUSTOMERS.SUBSCRIPTION.TITLE' | translate }}
          </h2>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {{ selectedCustomer()?.fullName }}
          </p>
        </div>
        <button
          (click)="showSubscriptionDialog.set(false)"
          class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
        >
          <lucide-icon [img]="X" class="w-5 h-5 text-gray-500"></lucide-icon>
        </button>
      </div>
      <form
        [formGroup]="subscriptionForm"
        (ngSubmit)="submitSubscription()"
        class="p-5 space-y-4 overflow-y-auto"
      >
        @if (errorMsg()) {
          <div
            class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400"
          >
            {{ errorMsg() }}
          </div>
        }
        <div>
          <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{
            'ADMIN.CUSTOMERS.SUBSCRIPTION.PLAN' | translate
          }}</label>
          <select
            formControlName="subscriptionPlanId"
            class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="">{{ 'ADMIN.CUSTOMERS.SUBSCRIPTION.SELECT_PLAN' | translate }}</option>
            @for (plan of plans(); track plan.id) {
              <option [value]="plan.id">
                {{ plan.name }} {{ plan.monthlyPrice | number: '1.0-0' }} UZS/oy
              </option>
            }
          </select>
        </div>
        <div>
          <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{
            'ADMIN.CUSTOMERS.SUBSCRIPTION.MONTHS' | translate
          }}</label>
          <div class="flex items-center gap-2">
            <input
              formControlName="monthsDuration"
              type="number"
              min="1"
              max="12"
              class="flex-1 px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <span class="text-sm text-gray-500 shrink-0">{{
              'ADMIN.CUSTOMERS.SUBSCRIPTION.MONTHS_UNIT' | translate
            }}</span>
          </div>
        </div>
        @if (calcLoading()) {
          <div class="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl text-center text-sm text-gray-500">
            {{ 'ADMIN.CUSTOMERS.SUBSCRIPTION.LOADING_PRICE' | translate }}
          </div>
        } @else if (pricePreview()) {
          <div
            class="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl space-y-2"
          >
            <p class="text-sm font-semibold text-green-900 dark:text-green-200">
              {{ 'ADMIN.CUSTOMERS.SUBSCRIPTION.PRICE_TITLE' | translate }}
            </p>
            <div class="space-y-1 text-sm">
              <div class="flex justify-between text-gray-700 dark:text-gray-300">
                <span>{{ pricePreview()!.planName }} Г— {{ pricePreview()!.months }} oy</span>
                <span>{{ formatMoney(pricePreview()!.baseAmount) }}</span>
              </div>
              @if (pricePreview()!.discountPercent > 0) {
                <div class="flex justify-between text-green-700 dark:text-green-400">
                  <span>{{
                    'ADMIN.CUSTOMERS.SUBSCRIPTION.DISCOUNT'
                      | translate: { percent: pricePreview()!.discountPercent }
                  }}</span>
                  <span>&minus;{{ formatMoney(pricePreview()!.discountAmount) }}</span>
                </div>
              }
              <div
                class="flex justify-between font-bold text-green-900 dark:text-green-200 pt-1 border-t border-green-200 dark:border-green-700"
              >
                <span>{{ 'ADMIN.CUSTOMERS.SUBSCRIPTION.TOTAL' | translate }}</span>
                <span>{{ formatMoney(pricePreview()!.totalAmount) }}</span>
              </div>
            </div>
          </div>
        }
        <div>
          <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{
            'ADMIN.CUSTOMERS.SUBSCRIPTION.NOTES' | translate
          }}</label>
          <input
            formControlName="notes"
            type="text"
            [placeholder]="'ADMIN.CUSTOMERS.SUBSCRIPTION.NOTES_HINT' | translate"
            class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div class="flex gap-3 pt-1">
          <button
            type="button"
            (click)="showSubscriptionDialog.set(false)"
            class="flex-1 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            {{ 'ADMIN.CUSTOMERS.SUBSCRIPTION.CANCEL' | translate }}
          </button>
          <button
            type="submit"
            [disabled]="subscriptionForm.invalid || submitting()"
            class="flex-1 py-2.5 text-sm bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 transition-colors font-medium"
          >
            {{
              submitting()
                ? ('ADMIN.CUSTOMERS.SUBSCRIPTION.SAVING' | translate)
                : ('ADMIN.CUSTOMERS.SUBSCRIPTION.SAVE' | translate)
            }}
          </button>
        </div>
      </form>
    </div>
  </div>
}

<!-- Extend Subscription Dialog -->
@if (showExtendDialog()) {
  <div class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
    <div
      class="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-[90vh] flex flex-col"
    >
      <div
        class="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700 shrink-0"
      >
        <div>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
            {{ 'ADMIN.CUSTOMERS.EXTEND.TITLE' | translate }}
          </h2>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {{ selectedCustomer()?.fullName }}
          </p>
        </div>
        <button
          (click)="showExtendDialog.set(false)"
          class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
        >
          <lucide-icon [img]="X" class="w-5 h-5 text-gray-500"></lucide-icon>
        </button>
      </div>
      <form
        [formGroup]="extendForm"
        (ngSubmit)="submitExtend()"
        class="p-5 space-y-4 overflow-y-auto"
      >
        @if (errorMsg()) {
          <div
            class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400"
          >
            {{ errorMsg() }}
          </div>
        }
        @if (selectedCustomer()?.subscriptionEndDate) {
          <div
            class="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl text-sm text-gray-600 dark:text-gray-400"
          >
            {{ 'ADMIN.CUSTOMERS.EXTEND.CURRENT_END' | translate }}
            <span class="font-medium text-gray-900 dark:text-white">{{
              formatDate(selectedCustomer()!.subscriptionEndDate)
            }}</span>
          </div>
        }
        <div>
          <p class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {{ 'ADMIN.CUSTOMERS.EXTEND.PLAN_CURRENT' | translate }}
          </p>
          <input
            type="text"
            [value]="
              selectedCustomer()?.subscriptionPlanName ??
              ('ADMIN.CUSTOMERS.EXTEND.NO_PLAN' | translate)
            "
            readonly
            class="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 cursor-default"
          />
        </div>
        <div
          class="flex items-center gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full sm:w-fit"
        >
          <button
            type="button"
            (click)="extendMode.set('months')"
            class="flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            [class.bg-white]="extendMode() === 'months'"
            [class.dark:bg-gray-900]="extendMode() === 'months'"
            [class.text-green-700]="extendMode() === 'months'"
            [class.dark:text-green-400]="extendMode() === 'months'"
            [class.shadow-sm]="extendMode() === 'months'"
            [class.text-gray-600]="extendMode() !== 'months'"
            [class.dark:text-gray-300]="extendMode() !== 'months'"
          >
            Oylik uzaytirish
          </button>
          <button
            type="button"
            (click)="extendMode.set('days')"
            class="flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            [class.bg-white]="extendMode() === 'days'"
            [class.dark:bg-gray-900]="extendMode() === 'days'"
            [class.text-green-700]="extendMode() === 'days'"
            [class.dark:text-green-400]="extendMode() === 'days'"
            [class.shadow-sm]="extendMode() === 'days'"
            [class.text-gray-600]="extendMode() !== 'days'"
            [class.dark:text-gray-300]="extendMode() !== 'days'"
          >
            Kunlik uzaytirish
          </button>
        </div>

        @if (extendMode() === 'months') {
          <div class="space-y-3">
            <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >Oylik muddat</label
            >
            <div class="flex items-center gap-2">
              <button
                type="button"
                (click)="decreaseExtendMonths()"
                class="h-11 w-11 rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                −
              </button>
              <input
                type="number"
                min="1"
                max="24"
                [(ngModel)]="extendMonthlyValue"
                (ngModelChange)="extendForm.patchValue({ monthsDuration: $event })"
                [ngModelOptions]="{ standalone: true }"
                class="w-full h-11 px-3 text-sm border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <button
                type="button"
                (click)="increaseExtendMonths()"
                class="h-11 w-11 rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                +
              </button>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs sm:text-sm">
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-800">
                <div class="text-gray-500 dark:text-gray-400">Hozirgi tugash</div>
                <div class="font-semibold text-gray-900 dark:text-white">
                  {{
                    selectedCustomer()?.subscriptionEndDate
                      ? (selectedCustomer()!.subscriptionEndDate | date: 'dd.MM.yyyy')
                      : '—'
                  }}
                </div>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-800">
                <div class="text-gray-500 dark:text-gray-400">Qo'shiladigan oy</div>
                <div class="font-semibold text-gray-900 dark:text-white">
                  {{ extendMonthlyValue }} oy
                </div>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-800">
                <div class="text-gray-500 dark:text-gray-400">Yangi tugash</div>
                <div class="font-semibold text-gray-900 dark:text-white">
                  {{ getExtendMonthlySummary().newEnd | date: 'dd.MM.yyyy' }}
                </div>
              </div>
            </div>
            <p class="text-xs text-green-700 dark:text-green-400">
              Qolgan kunlar:
              <span class="font-semibold">{{ getExtendMonthlySummary().daysLeft }}</span> kun. Oylik
              uzaytirish qolgan muddat ustiga qo'shiladi.
            </p>
          </div>
        } @else {
          <div>
            <p class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Kun tanlang
            </p>
            <p-datepicker
              [(ngModel)]="extendEndDateValue"
              (ngModelChange)="onExtendDateChange($event)"
              [ngModelOptions]="{ standalone: true }"
              [minDate]="minExtendDate()"
              dateFormat="dd.mm.yy"
              [showIcon]="true"
              [iconDisplay]="'input'"
              appendTo="body"
              placeholder="Kun tanlang"
              class="w-full"
              inputStyleClass="text-sm"
            />
            @if (extendEndDateValue) {
              <p class="text-xs text-green-600 dark:text-green-400 mt-1.5">
                Tanlangan kunlar:
                <span class="font-semibold">{{ computeDaysFromDate(extendEndDateValue) }}</span>
              </p>
            }
          </div>
        }
        @if (extendCalcLoading()) {
          <div class="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl text-center text-sm text-gray-500">
            {{ 'ADMIN.CUSTOMERS.EXTEND.LOADING_PRICE' | translate }}
          </div>
        } @else if (extendPricePreview()) {
          <div
            class="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl space-y-2"
          >
            <p class="text-sm font-semibold text-green-900 dark:text-green-200">
              {{ 'ADMIN.CUSTOMERS.SUBSCRIPTION.PRICE_TITLE' | translate }}
            </p>
            <div class="space-y-1 text-sm">
              <div class="flex justify-between text-gray-700 dark:text-gray-300">
                <span
                  >{{ extendPricePreview()!.planName }} Г—
                  {{ extendPricePreview()!.months }} oy</span
                >
                <span>{{ formatMoney(extendPricePreview()!.baseAmount) }}</span>
              </div>
              @if (extendPricePreview()!.discountPercent > 0) {
                <div class="flex justify-between text-green-700 dark:text-green-400">
                  <span>{{
                    'ADMIN.CUSTOMERS.SUBSCRIPTION.DISCOUNT'
                      | translate: { percent: extendPricePreview()!.discountPercent }
                  }}</span>
                  <span>&minus;{{ formatMoney(extendPricePreview()!.discountAmount) }}</span>
                </div>
              }
              <div
                class="flex justify-between font-bold text-green-900 dark:text-green-200 pt-1 border-t border-green-200 dark:border-green-700"
              >
                <span>{{ 'ADMIN.CUSTOMERS.SUBSCRIPTION.TOTAL' | translate }}</span>
                <span>{{ formatMoney(extendPricePreview()!.totalAmount) }}</span>
              </div>
            </div>
            <div
              class="pt-1 border-t border-green-200 dark:border-green-700 text-xs text-green-700 dark:text-green-400"
            >
              Yangi tugash sanasi:
              <span class="font-semibold">{{
                extendEndDateValue ? (extendEndDateValue | date: 'dd.MM.yyyy') : '—'
              }}</span>
            </div>
          </div>
        }
        <div>
          <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{
            'ADMIN.CUSTOMERS.EXTEND.NOTES' | translate
          }}</label>
          <input
            formControlName="notes"
            type="text"
            [placeholder]="'ADMIN.CUSTOMERS.SUBSCRIPTION.NOTES_HINT' | translate"
            class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div class="flex gap-3 pt-1">
          <button
            type="button"
            (click)="showExtendDialog.set(false)"
            class="flex-1 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            {{ 'ADMIN.CUSTOMERS.EXTEND.CANCEL' | translate }}
          </button>
          <button
            type="submit"
            [disabled]="extendForm.invalid || submitting()"
            class="flex-1 py-2.5 text-sm bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 transition-colors font-medium"
          >
            {{
              submitting()
                ? ('ADMIN.CUSTOMERS.EXTEND.SAVING' | translate)
                : ('ADMIN.CUSTOMERS.EXTEND.SAVE' | translate)
            }}
          </button>
        </div>
      </form>
    </div>
  </div>
}

<!-- Edit Customer Dialog -->
@if (showEditDialog()) {
  <div class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
    <div
      class="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-[90vh] flex flex-col"
    >
      <div
        class="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700 shrink-0"
      >
        <div>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Mijozni tahrirlash</h2>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {{ selectedCustomer()?.fullName }}
          </p>
        </div>
        <button
          (click)="showEditDialog.set(false)"
          class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
        >
          <lucide-icon [img]="X" class="w-5 h-5 text-gray-500"></lucide-icon>
        </button>
      </div>
      <form [formGroup]="editForm" (ngSubmit)="submitEdit()" class="p-5 space-y-4 overflow-y-auto">
        @if (errorMsg()) {
          <div
            class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400"
          >
            {{ errorMsg() }}
          </div>
        }
        <label
          class="flex items-center justify-between gap-3 rounded-xl border border-gray-200 dark:border-gray-700 p-3"
        >
          <span>
            <span class="block text-sm font-medium text-gray-900 dark:text-white"
              >Tenant aktiv</span
            >
            <span class="block text-xs text-gray-500 dark:text-gray-400">
              O'chirilsa butun tenant tizimga kira olmaydi.
            </span>
          </span>
          <input
            formControlName="isActive"
            type="checkbox"
            class="h-5 w-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
          />
        </label>
        <div>
          <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            To'lliq Isim familiya
          </label>
          <input
            formControlName="fullName"
            type="text"
            class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div>
          <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Telefon raqam
          </label>
          <input
            formControlName="phoneNumber"
            type="text"
            class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div>
          <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Yangi parol
          </label>
          <input
            formControlName="password"
            type="password"
            placeholder="Bo'sh qoldirilsa o'zgarmaydi"
            class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div class="flex gap-3 pt-1">
          <button
            type="button"
            (click)="showEditDialog.set(false)"
            class="flex-1 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Bekor qilish
          </button>
          <button
            type="submit"
            [disabled]="editForm.invalid || submitting()"
            class="flex-1 py-2.5 text-sm bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 transition-colors font-medium"
          >
            {{ submitting() ? 'Saqlanmoqda...' : 'Saqlash' }}
          </button>
        </div>
      </form>
    </div>
  </div>
}

<!-- Delete Customer Dialog (SuperAdmin only) -->
@if (showDeleteDialog()) {
  <div class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
    <div
      class="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-[90vh] flex flex-col"
    >
      <div
        class="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700 shrink-0"
      >
        <div class="flex items-center gap-2">
          <lucide-icon [img]="Trash2" class="w-5 h-5 text-red-600"></lucide-icon>
          <h2 class="text-lg font-semibold text-red-600">Mijozni butunlay o‘chirish</h2>
        </div>
        <button
          (click)="showDeleteDialog.set(false)"
          class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
        >
          <lucide-icon [img]="X" class="w-5 h-5 text-gray-500"></lucide-icon>
        </button>
      </div>
      <div class="p-5 space-y-4 overflow-y-auto">
        @if (errorMsg()) {
          <div
            class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400"
          >
            {{ errorMsg() }}
          </div>
        }
        <div
          class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400"
        >
          <p class="font-medium">Diqqat! Bu amalni qaytarib bo‘lmaydi.</p>
          <p class="mt-1">
            <strong>{{ selectedCustomer()?.businessName }}</strong> mijoziga tegishli barcha
            ma'lumotlar (do‘konlar, mahsulotlar, buyurtmalar, mijozlar, moliya, xodimlar va login
            hisobi) butunlay o‘chiriladi.
          </p>
        </div>
        <div>
          <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Tasdiqlash uchun biznes nomini kiriting:
            <span class="font-semibold text-gray-900 dark:text-white">{{
              selectedCustomer()?.businessName
            }}</span>
          </label>
          <input
            [ngModel]="deleteConfirmText()"
            (ngModelChange)="deleteConfirmText.set($event)"
            type="text"
            placeholder="Biznes nomi"
            class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>
        <div class="flex gap-3 pt-1">
          <button
            type="button"
            (click)="showDeleteDialog.set(false)"
            class="flex-1 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Bekor qilish
          </button>
          <button
            type="button"
            (click)="confirmDelete()"
            [disabled]="
              submitting() || deleteConfirmText().trim() !== selectedCustomer()?.businessName
            "
            class="flex-1 py-2.5 text-sm bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors font-medium"
          >
            {{ submitting() ? 'O‘chirilmoqda...' : 'Butunlay o‘chirish' }}
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Product Import Dialog -->
<!--
  Import oynasi — `shared/components/product-import`. Mijozning o'z do'konlar
  sahifasi ham AYNAN shu komponentni ishlatadi; farqi faqat qaysi endpointga
  borishida (bu yerda admin, u yerda tenant JWT dan).
-->
<app-product-import-dialog
  [(visible)]="showImportDialog"
  [subtitle]="selectedCustomer()?.businessName ?? ''"
  [stores]="importStoreOptions()"
  [currencyOptions]="importCurrencyOptions()"
  [defaultCurrency]="importDefaultCurrency()"
  [previewFn]="importPreviewFn"
  [submitFn]="importSubmitFn"
  (imported)="onProductsImported($event)"
/>

<!--
  Katalog tekshiruvi. Import validatsiyasi keyinroq qo'shilgani uchun undan oldin
  yaratilgan mahsulotlarda xatolar qolgan bo'lishi mumkin — bu oyna ularni topib
  beradi. Hech narsa o'zgartirilmaydi, faqat ro'yxat.
-->
@if (showCheckDialog()) {
  <div class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
    <div
      class="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-2xl max-h-[90vh] flex flex-col"
    >
      <div
        class="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700 shrink-0"
      >
        <div class="flex items-center gap-2 min-w-0">
          <lucide-icon [img]="ShieldCheck" class="w-5 h-5 text-amber-600 shrink-0"></lucide-icon>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white truncate">
            Mahsulotlar tekshiruvi
            <span class="font-normal text-gray-500">— {{ selectedCustomer()?.businessName }}</span>
          </h2>
        </div>
        <button
          (click)="showCheckDialog.set(false)"
          class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg shrink-0"
        >
          <lucide-icon [img]="X" class="w-5 h-5 text-gray-500"></lucide-icon>
        </button>
      </div>

      <div class="p-5 space-y-4 overflow-y-auto">
        @if (errorMsg()) {
          <div
            class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400"
          >
            {{ errorMsg() }}
          </div>
        }

        @if (checking()) {
          <p class="text-sm text-gray-500 py-6 text-center">Tekshirilmoqda...</p>
        } @else if (checkResult(); as res) {
          <div class="flex items-center gap-3 text-sm">
            <span class="text-gray-500">Jami mahsulot:</span>
            <span class="font-semibold text-gray-900 dark:text-white">{{ res.totalProducts }}</span>
            <span class="text-gray-300">|</span>
            <span class="text-gray-500">Muammoli:</span>
            <span
              class="font-semibold"
              [class]="res.problemProductCount > 0 ? 'text-amber-600' : 'text-green-600'"
            >
              {{ res.problemProductCount }}
            </span>
          </div>

          @if (res.issues.length === 0) {
            <div
              class="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-sm text-green-800 dark:text-green-300 flex items-center gap-2"
            >
              <lucide-icon [img]="CheckCircle" class="w-4 h-4 shrink-0"></lucide-icon>
              Hamma mahsulot qoidalarga mos — muammo topilmadi.
            </div>
          } @else {
            <div class="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table class="w-full text-xs">
                <thead>
                  <tr
                    class="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
                  >
                    <th class="px-3 py-2 text-left font-semibold">Mahsulot</th>
                    <th class="px-3 py-2 text-left font-semibold">Barkod</th>
                    <th class="px-3 py-2 text-left font-semibold">Muammo</th>
                  </tr>
                </thead>
                <tbody>
                  @for (i of res.issues; track $index) {
                    <tr class="border-b last:border-0 border-gray-100 dark:border-gray-700">
                      <td class="px-3 py-2 text-gray-900 dark:text-gray-100">
                        {{ i.name }}
                        @if (i.brand) {
                          <span class="text-gray-400">({{ i.brand }})</span>
                        }
                      </td>
                      <td class="px-3 py-2 text-gray-500 whitespace-nowrap">{{ i.barcode }}</td>
                      <td class="px-3 py-2 text-amber-700 dark:text-amber-400">{{ i.message }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        }

        <div class="flex gap-3 pt-1">
          <button
            type="button"
            (click)="showCheckDialog.set(false)"
            class="flex-1 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Yopish
          </button>
          @if (checkResult()?.issues?.length) {
            <button
              type="button"
              (click)="copyCheckReport()"
              class="flex-1 py-2.5 text-sm bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors font-medium"
            >
              {{ checkCopied() ? 'Nusxalandi' : 'Hisobotni nusxalash' }}
            </button>
          }
        </div>
      </div>
    </div>
  </div>
}

<!-- Payment History Dialog -->
@if (showPaymentsDialog()) {
  <div class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
    <div
      class="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[85vh] flex flex-col"
    >
      <div
        class="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700 shrink-0"
      >
        <div>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
            {{ 'ADMIN.CUSTOMERS.PAYMENTS.TITLE' | translate }}
          </h2>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {{ selectedCustomer()?.fullName }}
          </p>
        </div>
        <button
          (click)="showPaymentsDialog.set(false)"
          class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
        >
          <lucide-icon [img]="X" class="w-5 h-5 text-gray-500"></lucide-icon>
        </button>
      </div>
      <div class="p-5 overflow-y-auto">
        @if (paymentsLoading()) {
          <p class="text-center text-gray-500 py-6">
            {{ 'ADMIN.CUSTOMERS.PAYMENTS.LOADING' | translate }}
          </p>
        } @else if (payments().length === 0) {
          <p class="text-center text-gray-400 py-6">
            {{ 'ADMIN.CUSTOMERS.PAYMENTS.EMPTY' | translate }}
          </p>
        } @else {
          <div class="space-y-3">
            @for (p of payments(); track p.id) {
              <div
                class="p-4 rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
              >
                <div class="flex items-start justify-between gap-2">
                  <div>
                    <p class="font-semibold text-gray-900 dark:text-white text-sm">
                      {{ p.planName }} {{ p.months }} oy
                    </p>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {{ formatDate(p.paidAt) }} В· {{ p.paymentMethod }}
                    </p>
                    @if (p.notes) {
                      <p class="text-xs text-gray-400 mt-0.5 italic">{{ p.notes }}</p>
                    }
                  </div>
                  <div class="text-right shrink-0">
                    @if (p.discountPercent > 0) {
                      <p class="text-xs text-gray-400 line-through">
                        {{ formatMoney(p.baseAmount) }}
                      </p>
                      <p class="text-xs text-green-600 dark:text-green-400">
                        &minus;{{ p.discountPercent }}%
                      </p>
                    }
                    <p class="font-bold text-gray-900 dark:text-white text-sm">
                      {{ formatMoney(p.totalAmount) }}
                    </p>
                  </div>
                </div>
              </div>
            }
          </div>
        }
      </div>
    </div>
  </div>
}
